<template>
  <form class="form pt-3 container-fluid" v-on:submit.prevent="validateFields($event)">
    <!-- address validation bypass code banner -->
    <div v-if="validationBypassState"
      id="validation-bypass-banner"
      class="w-100 mb-3 text-center">
      <div v-if="validationBypassState === 'valid'" class="banner banner-success rounded">
        <CmsText cms-key="user_verification:address_validation_bypass_success"/>
      </div>
      <div v-else class="banner banner-warning rounded">
        <CmsText cms-key="user_verification:address_validation_bypass_fail"/>
      </div>
    </div>

    <p v-if="!fullScreen">
      <CmsText cms-key="user_verification:explanation"></CmsText>
    </p>

    <QuestionList v-if="formFields.length && !isLoading"
      v-bind:formFields="primaryFields"
      v-bind:isNonFieldError="nonFieldErrors && !!nonFieldErrors.length"
      @clearNonFieldErrors="clearNonFieldErrors"
      v-model="formSubmitFields"/>

    <!-- Additional verifications fields are rendered separately and under certain conditions -->
    <div v-if="verificationAttempts && additionalFields.length"
      v-bind:class="{'attention-container': !hasReachedVerificationAttemptLimit}">
      <CmsText class="attention-title"
        v-if="!hasReachedVerificationAttemptLimit"
        cms-key="user_verification:additional_verification_title"/>
      <QuestionList v-if="formFields.length && !isLoading"
        v-bind:formFields="additionalFields"
        v-bind:isNonFieldError="nonFieldErrors && !!nonFieldErrors.length"
        @clearNonFieldErrors="clearNonFieldErrors"
        v-model="formSubmitFields"/>
    </div>

    <!-- show if loading -->
    <div class="loader-on-white">
      <Spinner v-bind:isLoading="isLoading"/>
    </div>

    <div class="form-group" v-if="displayNonFieldError">
      <span class="invalid-feedback" v-html="nonFieldErrors[0]"></span>
    </div>

      <div class="compliance-disclosure bg-light rounded mb-3" v-if="complianceDisclosuresEnabled">
        <svg class="compliance-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1008 1008">
          <!-- eslint-disable-next-line -->
          <path d="M311 486l-59 59 167 170 337-337-59-59-278 276zm-185-23V211L504 41l378 170v252c0 116-35 224-108 321-73 96-162 157-270 183-108-26-197-87-270-183-73-97-108-205-108-321z"></path>
        </svg>
        <div class="disclosure" data-test="compliance-disclosure">
          <CmsText cms-key="compliance_disclosure" :default-value="defaultTexts.COMPLIANCE_DISCLOSURE"></CmsText>
        </div>
    </div>

    <div class="row align-items-center sticky-button-container bg-white py-3 border-top mx-0">
      <div class="col pl-0">
        <span v-if="currentFormsLength && !fullScreen" class="text-muted">
          Step {{ currentStep }} of {{ currentFormsLength }}
        </span>
      </div>
      <div class="col-auto pr-0">
        <CancelButton location="verification-form" v-if="!fullScreen">
          <CmsText cms-key="user_verification:cancel_button_text" :default-value="defaultTexts.CANCEL_BUTTON"></CmsText>
        </CancelButton>
        <TransitionButton v-if="ocrEnabled && (!!nonFieldErrors.length || !!errors.items.length)">
          <CmsText cms-key="user_verification:transition_button_text" :default-value="defaultTexts.TRANSITION_BUTTON"></CmsText>
        </TransitionButton>
        <SubmitButton v-bind:inSubmission="inSubmission" :class="{ fullscreen: fullScreen }">
          <CmsText cms-key="user_verification:continue_button_text" :default-value="defaultTexts.CONTINUE_BUTTON"></CmsText>
        </SubmitButton>
      </div>
    </div>
  </form>
</template>

<script>
import FormMixin from '@/mixins/FormMixin';
import QuestionList from '@/components/QuestionList.vue';
import SubmitButton from '@/components/SubmitButton.vue';
import CancelButton from '@/components/CancelButton.vue';
import TransitionButton from '@/components/TransitionButton.vue';
import CmsText from '@/components/CmsText.vue';
import Spinner from '@/components/Spinner.vue';
import constants from '@/constants';
import { mapState, mapMutations, mapActions } from 'vuex';

/**
 * User Validation Form
 *
 * This form is used to validate users against a customizable set of questions.
 */
export default {
  name: 'UserValidationForm',
  components: {
    QuestionList,
    SubmitButton,
    CancelButton,
    TransitionButton,
    CmsText,
    Spinner,
  },
  mixins: [
    FormMixin,
  ],
  data() {
    return {
      isLoading: false,
      validationBypassState: undefined,
      VERIFICATION_ATTEMPT_LIMIT: 2,
    };
  },
  computed: {
    ...mapState('forms', [
      'forms',
      'verificationAttempts',
    ]),
    ...mapState('customerData', ['validationData', 'validationBypassCode']),
    ...mapState('siteSettings', ['settings']),
    complianceDisclosuresEnabled() {
      return this.settings?.rebateConfig?.complianceDisclosuresEnabled;
    },
    currentFormsLength() {
      return this.getCurrentForms().length;
    },
    currentStep() {
      return Number(this.getCurrentFormIndex()) + 1;
    },
    /**
     * If additional verification is enabled, determine whether
     * or not a sufficient amount of verification attempts have
     * happened to display non-field errors.
     */
    displayNonFieldError() {
      if (!this.nonFieldErrors.length) {
        return false;
      }
      if (!this.additionalFields.length) {
        return true;
      }
      return this.hasReachedVerificationAttemptLimit;
    },
    // Get the main form fields to be displayed.
    primaryFields() {
      return this.formFields.filter(field => !field.additional_verification) || [];
    },
    // Get the additional verification fields to be displayed
    additionalFields() {
      return this.formFields.filter(field => field.additional_verification) || [];
    },
    /**
     * Determine if the user has reached the verification attempt limit
     *
     * @return {Boolean}
     */
    hasReachedVerificationAttemptLimit() {
      return this.verificationAttempts >= this.VERIFICATION_ATTEMPT_LIMIT;
    },
    defaultTexts() {
      return constants.defaultTexts;
    },
  },
  methods: {
    ...mapMutations('customerData', [
      'setValidationData',
      'removeValidationBypassCode',
    ]),
    ...mapMutations('forms', ['disableFormFields']),
    ...mapActions('customerData', ['fetchValidationData', 'fetchValidationBypassData']),
    // Validate the fields of the current form, and if they are successful,
    // submit the form's data to the backend.
    validateFields(event) {
      event.preventDefault();
      this.$validator.validate().then((result) => {
        if (result) {
          this.setValidationData(this.formSubmitFields);
          this.submitFormData(this.formObject.type).finally(this.gotoError);
        }
        return result;
      });
    },
    // Scroll to first error field on page if it exists
    gotoError() {
      const element = this.$el.querySelector('.has-error');
      // if there are errors, scroll to first error
      if (element) {
        const top = element.offsetTop;
        window.scrollTo(0, top);
      }
    },
    // Gets form to be created
    createForm() {
      if (this.forms.length > 0) {
        this.initializeForm();
      }
    },
    // Updates address fields based on either stored customer data,
    // or data tied to the validation bypass code.
    updateFormAddress() {
      this.isLoading = true;
      // Get address info from bypass code endpoint
      if (this.validationBypassCode) {
        const utilityID = this.forms[0].utility_id;
        this.fetchValidationBypassData({ bypassCode: this.validationBypassCode, utilityID })
          .then((data) => {
            data.is_valid ? this.handleGoodBypassCode() : this.handleBadBypassCode();
          })
          .catch(this.handleBadBypassCode);
      } else {
        // Get validation data from RRAPI backend
        this.fetchValidationData(this.customerId)
          .finally(() => { this.isLoading = false; });
      }
    },
    handleGoodBypassCode() {
      this.validationBypassState = 'valid';
      this.isLoading = false;
      const bypassedFields = ['full_name', 'account_number', 'address', 'phone_number'];
      // Disable the bypassed fields
      this.disableFormFields({
        fieldNames: bypassedFields,
        formIndex: 0,
      });
    },
    handleBadBypassCode() {
      // Remove code from store
      this.removeValidationBypassCode();
      this.validationBypassState = 'invalid';
    },
    // Clears errors that might block the user from re-submitting the form.
    clearNonFieldErrors() {
      this.filterOutNonFieldErrors();
    },
    /**
     * Adds custom errors to the additional verification fields.
     * Error text is from the CMS.
     */
    addAdditionalVerificationErrors() {
      const DEFAULT_KEY = 'default';
      // Dynamically create a CMS key
      const formatCMSKey = key => `user_verification:additional_verification_${key}_field_error`;
      // Dynamically retrieve a CMS text or fallback to a default text from CMS.
      const getFieldErrorMsg = fieldKey => (
        this.getCMSText(formatCMSKey(fieldKey)) || this.getCMSText(formatCMSKey(DEFAULT_KEY))
      );
      // Loop through additional verification field keys and add CMS error text to field.
      this.additionalFields.forEach((field) => {
        const errorMsg = getFieldErrorMsg(field.key);
        this.errors.add({
          field: field.key,
          msg: errorMsg,
        });
      });
    },
    /**
     * Track secondary auth attempts via segment.io
     */
    trackAttempts() {
      const properties = {};
      const verificationState = this.hasReachedVerificationAttemptLimit ? 'Failed' : 'Triggered';
      const EVENT = `Secondary Verification ${verificationState}`;
      if (verificationState === 'Failed') {
        properties.additionalVerificationUsed = this.additionalVerificationUsed;
      }
      this.trackEvent(EVENT, properties);
    },
  },
  watch: {
    forms() {
      this.createForm();
    },
    validationData() {
      this.formSubmitFields = { ...{}, ...this.validationData };
    },
    // Necessary listener to re-run customer data fetch when invalid bypass code is
    // removed from the store.
    validationBypassCode() {
      this.updateFormAddress();
    },
    formFields() {
      this.updateFormAddress();
      this.trackCurrentFormEvent(this.segment.constants.events.OPENED);
    },
    verificationAttempts() {
      // Since we only want to display this special field error the first time a user
      // fails utility verification, only run if attempts is < the attempt limit.
      if (this.verificationAttempts < this.VERIFICATION_ATTEMPT_LIMIT) {
        this.addAdditionalVerificationErrors();
      }
      this.trackAttempts();
    },
  },
  created() {
    this.createForm();
    this.formSubmitFields = { ...{}, ...this.validationData };
  },
};
</script>
